import { Suspense, useEffect } from 'react';

import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { BrowserRouter } from 'react-router-dom';

import { InitializationProvider } from 'components/InitializationProvider';
import ThemeFallback from 'components/ThemeFallback';
import { Toaster } from 'components/ui/sonner';
import ViewportHeightProvider from 'components/ViewportHeightProvider';
import ProviderContext from 'contexts';
import { MeetingProvider } from 'global/MeetingContext';
import { useLocale } from 'hooks/useLocale';
import { usePageTitle } from 'hooks/usePageTitle';
import { createQueryClient, createPersister, PERSIST_MAX_AGE } from 'lib/query';
import { ThemeProvider } from 'providers/ThemeProvider';
import AuthRoutes from 'routes/AuthRoutes';
import { logger } from 'utils/logger';

// Expected computed background-color values from design tokens
const DARK_BG_RGB = 'rgb(10, 10, 10)';
const TRANSPARENT_BG = 'rgba(0, 0, 0, 0)';

// Create a client for React Query using centralized configuration
const queryClient = createQueryClient();

// Create persister for localStorage persistence
const persister = createPersister();

const App = () => {
  usePageTitle();
  useLocale();

  useEffect(() => {
    // Fix ResizeObserver error without causing recursion
    const resizeObserverLoopErr = /ResizeObserver loop limit exceeded/;
    const originalConsoleError = console.error;

    // eslint-disable-next-line no-console
    console.error = (...args) => {
      if (args[0] && resizeObserverLoopErr.test(args[0])) {
        // Ignore the ResizeObserver error (browser quirk)
        // Using originalConsoleError.call directly to avoid recursion
        return;
      }
      // Log other errors using originalConsoleError to avoid infinite recursion
      originalConsoleError(...args);
    };

    // Theme monitoring WITHOUT automatic reload
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // When the page becomes visible again, only monitor the theme
        const bodyBackground = getComputedStyle(document.body).backgroundColor;
        const expectedBackground = DARK_BG_RGB;

        if (bodyBackground !== expectedBackground && bodyBackground !== TRANSPARENT_BG) {
          logger.warn(
            'Theme issue detected - user can manually reload if needed',
            {
              currentBackground: bodyBackground,
              expectedBackground,
              timestamp: new Date().toISOString(),
            }
          );
          // NO automatic reload - log only for debugging
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSIST_MAX_AGE,
      }}
    >
      <BrowserRouter>
        <Suspense
          fallback={
            <div className="flex h-screen items-center justify-center bg-background text-foreground">
              Loading...
            </div>
          }
        >
          <ProviderContext>
            <ThemeProvider>
              <ThemeFallback>
                <ViewportHeightProvider>
                  <InitializationProvider>
                    <MeetingProvider>
                      <AuthRoutes />
                    </MeetingProvider>
                  </InitializationProvider>
                </ViewportHeightProvider>
                {/* Sonner Toast Notifications (shadcn/ui) */}
                <Toaster richColors closeButton />
              </ThemeFallback>
            </ThemeProvider>
          </ProviderContext>
        </Suspense>
      </BrowserRouter>
    </PersistQueryClientProvider>
  );
};

export default App;
